import { isEmpty, isEqual, noop } from "lodash";
import React, { Dispatch, useEffect, useId, useMemo, useState } from "react";

import {
  AvailableIntegrationFragment,
  AvroSchemaFieldDto,
  HistoryStream,
  OperationMode,
  PlanName,
  SchemaDto,
  SchemasQuery,
  Stream,
  SubStream,
} from "@/apollo/types";
import {
  Button,
  IconButton,
  PrimaryButton,
  SecondaryButton,
} from "@/components/elements/Button";
import { BetaFeatureText } from "@/components/elements/FeatureStateText";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import { PlanFeatureBadge } from "@/components/elements/PlanFeatureBadge";
import Select from "@/components/elements/Select";
import Toggle from "@/components/elements/Toggle";
import Tooltip from "@/components/elements/Tooltip";
import { Checkbox } from "@/components/primitives/Checkbox";
import { useConnectionCDCFeaturesContext } from "@/entities/connections";
import { FullSyncScheduleModal } from "@/features/elt/elt-sync-tables/components/FullSyncScheduleModal";
import { hasFullSyncToReconcileAgainst } from "@/features/elt/fullSyncSchedule";
import { keptAdditionalCursors } from "@/features/elt/additionalCursors";
import { SourceStreamsAction } from "@/features/elt/reducers/sourceStreamsReducer";
import { useDataWarehouseSupportsSoftDeletes } from "@/features/elt/useDataWarehouseSoftDeletes";
import { weldManagedFields } from "@/features/elt/weldManagedFields";
import { useCurrentPlan } from "@/features/subscription";
import classNames from "@/helpers/classNames";
import { Menu, Switch } from "@headlessui/react";

import { StreamMode } from "../types/StreamMode";
import { HighlightedText } from "./HighlightedText";
import { OptionTooltips } from "./OptionTooltips";
import { DeprecatedLabel, SyncLabels } from "./SyncLabels";
import { useSourceStreamsState } from "./TableConfig";
import { TableOptionsMenu } from "./TableOptionsMenu";
import { DeprecatedHoverCard, HistoryTableHoverCard } from "@/features/elt";
import {
  ChevronDownIcon,
  History,
  ChevronRightIcon,
  CircleAlertIcon,
} from "lucide-react";

type StreamSchema = SchemasQuery["getAllAvroSchemas"]["schemas"][0];

function resolveIncrementalConfig(
  integrationId: string,
  schema?: StreamSchema,
): { cursor?: string; primaryKeys: string[] } | null {
  if (!schema) {
    return null;
  }

  if (integrationId.includes("ftp")) {
    const pkField = schema.fields.find((c) => c.name.match(/_id$/i))?.name;
    /**
     * By dispatching PK with empty array and cursor with a value it will always default to APPEND mode.
     * (Empty array as PK will convert to null in the backend.)
     */
    return {
      cursor: "_modified",
      primaryKeys: pkField ? [pkField] : [],
    };
  }

  if (!schema.primaryKeys?.length) {
    return null;
  }

  if (schema.cursor) {
    return {
      cursor: schema.cursor,
      primaryKeys: schema.primaryKeys,
    };
  }

  if (integrationId === "postgresql") {
    return {
      cursor: "xmin",
      primaryKeys: schema.primaryKeys,
    };
  }

  const UpdatedAtMatcherRegex =
    /\b(updated|updatedat|updated_at|updatetime|update_time|updatedate|update_date|updatedon|updated_on|modifiedat|modified_at|modified|modifieddate|modified_date|datemodified|date_modified|lastupdated|last_updated|lastmodified|last_modified|datemodified|date_modified|timestampupdated|timestamp_updated|updatetimestamp|update_timestamp|updatedtimestamp|updated_timestamp)\b/gi;
  const updatedAtField = schema.fields.find((c) =>
    c.name.match(UpdatedAtMatcherRegex),
  )?.name;
  if (updatedAtField) {
    return {
      cursor: updatedAtField,
      primaryKeys: schema.primaryKeys,
    };
  }
  return {
    primaryKeys: schema.primaryKeys,
  };
}

function initIncrementalConfig(props: {
  schema: StreamSchema;
  sourceIntegrationId: string;
  supportedModes: OperationMode[];
  configurableIncremental: boolean;
  dispatch: Dispatch<SourceStreamsAction>;
}) {
  const incrementalConfig = resolveIncrementalConfig(
    props.sourceIntegrationId,
    props.schema,
  );
  if (incrementalConfig) {
    props.dispatch({
      type: "set_incremental_config",
      payload: {
        cursor: incrementalConfig.cursor,
        primaryKeys: incrementalConfig.primaryKeys,
        sourceStreamName: props.schema.sourceStreamName,
        stream: {
          supportedModes: props.supportedModes,
          configurableIncremental: props.configurableIncremental,
        },
      },
    });
  }
}

export default function StreamItem(props: {
  stream: Stream;
  search: string;
  sourceIntegration: AvailableIntegrationFragment;
  schema?: StreamSchema;
  required?: boolean;
  /** The sync's own schedule, which the full sync schedule picker reads against. */
  syncInterval?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [state, dispatch] = useSourceStreamsState();
  const checked = !!state[props.stream.name] || !!props.required;
  const hasSubstreams = !!props.stream.substreams.length;
  const streamState = state[props.stream.name];
  const hasHistoryStreams =
    checked &&
    !!streamState?.enabledHistoryTable &&
    props.stream.historyStreams.length > 0;

  useEffect(() => {
    if (!checked) {
      setOpen(false);
    }
  }, [checked]);

  const schema = props.schema;

  /**
   * When the schema arrives (it may be lazy-loaded by the parent's batched
   * query after the stream is already checked), auto-detect the incremental
   * config if it hasn't been set up yet.
   */
  useEffect(() => {
    if (
      !schema ||
      !checked ||
      !props.stream.supportedModes.includes(OperationMode.Incremental) ||
      !props.stream.configurableIncremental
    ) {
      return;
    }

    if (
      streamState?.mode === "APPEND" || // If already append mode don't change it
      streamState?.mode === "FULL" || // If already full mode don't change it
      (streamState?.incrementalPointerId &&
        (streamState?.primaryKeys || []).length > 0)
    ) {
      return;
    }
    initIncrementalConfig({
      schema,
      sourceIntegrationId: props.sourceIntegration.id,
      supportedModes: props.stream.supportedModes,
      configurableIncremental: props.stream.configurableIncremental,
      dispatch,
    });
  }, [
    schema,
    checked,
    props.stream.supportedModes,
    props.stream.configurableIncremental,
    props.sourceIntegration.id,
    streamState?.incrementalPointerId,
    streamState?.primaryKeys,
    streamState?.mode,
    dispatch,
  ]);

  const hasColumns = checked && schema?.fields && schema.fields.length > 0;

  const onToggle = (checked: boolean) => {
    dispatch({
      type: "set_source_stream",
      payload: { checked, stream: props.stream },
    });

    if (!checked) {
      return;
    }
    if (
      schema &&
      props.stream.supportedModes.includes(OperationMode.Incremental) &&
      props.stream.configurableIncremental
    ) {
      initIncrementalConfig({
        schema,
        sourceIntegrationId: props.sourceIntegration.id,
        supportedModes: props.stream.supportedModes,
        configurableIncremental: props.stream.configurableIncremental,
        dispatch,
      });
    }
  };

  const checkboxId = useId();
  return (
    <Tooltip content={props.stream.notSelectableExplanation || null}>
      <div className={"relative text-sm"}>
        <div>
          {(hasSubstreams || hasHistoryStreams) && (
            <ConnectingLine isParent={true} />
          )}
          <div className="group flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <label className="flex w-full min-w-0 grow items-center gap-4 rounded-xs px-4 py-2">
              <Checkbox
                checked={checked}
                disabled={!props.stream.selectable}
                onChange={(e) => {
                  if (!props.stream.selectable) {
                    return;
                  }
                  onToggle(e.target.checked);
                }}
                aria-labelledby={`source-stream-checkbox-label__${checkboxId}`}
                id={`source-stream-checkbox__${checkboxId}`}
              />
              <div className="flex w-full min-w-0 items-center gap-2">
                {hasColumns && (
                  <OpenButton
                    open={open}
                    disabled={false}
                    onToggle={(e) => {
                      e.stopPropagation();
                      setOpen((prev) => !prev);
                    }}
                    checked={checked}
                  />
                )}
                {checked && !schema && <LoadingSpinner className="w-4" />}
                <div
                  id={`source-stream-checkbox-label__${checkboxId}`}
                  className={
                    "cursor-default truncate" +
                    (!props.stream.selectable
                      ? " text-gray-300 dark:text-gray-600"
                      : "")
                  }
                  title={props.stream.name}
                >
                  <HighlightedText
                    highlighted={props.search}
                    text={props.stream.name}
                    lineThrough={props.stream.retired}
                  />
                </div>
              </div>
              {props.required && (
                <div className="rounded-xs bg-primary px-1 text-1xs text-white">
                  Required
                </div>
              )}
            </label>
            {checked && (
              <div className="ml-auto pr-4">
                <TableOptions
                  stream={props.stream}
                  sourceIntegration={props.sourceIntegration}
                  schema={schema}
                  syncInterval={props.syncInterval}
                />
              </div>
            )}
          </div>
          {open ? (
            <div className="pb-2 pl-8">
              {schema?.fields?.map((o) => (
                <StreamSchemaOption
                  key={o.name}
                  option={o}
                  stream={props.stream}
                  schema={schema}
                  required={!!props.required}
                />
              ))}
              <WeldManagedSchemaOptions stream={props.stream} />
            </div>
          ) : null}
        </div>
        {(hasSubstreams || hasHistoryStreams) && (
          <div className="">
            {[
              ...props.stream.substreams.map((substream) => ({
                type: "substream" as const,
                name: substream.destinationTableName,
                substream,
              })),
              ...(hasHistoryStreams
                ? props.stream.historyStreams.map((historyStream) => ({
                    type: "history" as const,
                    name: historyStream.destinationTableName,
                    historyStream,
                  }))
                : []),
            ]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item, index, arr) =>
                item.type === "substream" ? (
                  <SubStreamItem
                    key={item.substream.id}
                    substream={item.substream}
                    isLast={index === arr.length - 1}
                    search={props.search}
                    stream={props.stream}
                    checked={checked}
                  />
                ) : (
                  <HistoryStreamItem
                    key={item.historyStream.destinationTableName}
                    historyStream={item.historyStream}
                    isLast={index === arr.length - 1}
                    search={props.search}
                    stream={props.stream}
                  />
                ),
              )}
          </div>
        )}
      </div>
    </Tooltip>
  );
}

const TableOptions = (props: {
  stream: Stream;
  sourceIntegration: AvailableIntegrationFragment;
  schema?: StreamSchema;
  syncInterval?: string | null;
}) => {
  const [state, dispatch] = useSourceStreamsState();
  const streamState = state[props.stream.name];
  const [showIncrementalSetup, setShowIncrementalSetup] = useState(false);
  const [scheduleModal, setScheduleModal] = useState<{
    requiredForCaptureDeletes: boolean;
  } | null>(null);

  const mode: StreamMode = useMemo(() => {
    if (streamState?.changeDataCaptureEnabled) {
      return StreamMode.CDC;
    }
    if (streamState?.fullSyncAlways) {
      return StreamMode.MANUAL_FULL;
    }
    if (streamState && props.stream.configurableIncremental) {
      if (
        streamState.incrementalPointerId &&
        isEmpty(streamState.primaryKeys)
      ) {
        return StreamMode.APPEND;
      }
      return StreamMode.INCREMENTAL;
    }

    const supportedModes = props.stream.supportedModes;
    if (supportedModes.includes(OperationMode.Incremental)) {
      return StreamMode.INCREMENTAL;
    }
    if (supportedModes.includes(OperationMode.Append)) {
      return StreamMode.APPEND;
    }
    return StreamMode.FIXED_FULL;
  }, [
    props.stream.supportedModes,
    props.stream.configurableIncremental,
    streamState,
  ]);

  const showIncrementalOption = props.stream.configurableIncremental;
  const showAlwaysFullOption =
    mode !== StreamMode.CDC && mode !== StreamMode.FIXED_FULL;
  const showFullSyncScheduleOption =
    mode !== StreamMode.CDC &&
    [StreamMode.INCREMENTAL, StreamMode.APPEND].includes(mode);

  const warehouseSupportsSoftDeletes = useDataWarehouseSupportsSoftDeletes();
  const showSoftDeleteOption =
    !!props.stream.softDeleteSupported && warehouseSupportsSoftDeletes;

  const showProtectedOption =
    ![StreamMode.FIXED_FULL, StreamMode.MANUAL_FULL].includes(mode) &&
    !streamState?.fullSyncSchedule;

  const connectionCDCFeatures = useConnectionCDCFeaturesContext();

  const { features } = useCurrentPlan();

  // `connectionCDCFeatures` reflects the source database prerequisites and connection-level CDC state;
  // account entitlement is checked separately via `features.changeDataCapture` below. Don't read the
  // retired `changeDataCaptureEnabled` LaunchDarkly flag here, because deleting it would hide this option from every account.
  const showCDCOption =
    props.sourceIntegration.eltSource?.changeDataCaptureSupported &&
    connectionCDCFeatures.changeDataCaptureEnabled &&
    connectionCDCFeatures.supportedTables?.includes(props.stream.name);

  return (
    <div
      className="flex items-center justify-end gap-2"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {streamState?.isIncrementalInvalid ? (
        <div className="flex h-full flex-row items-center">
          <Tooltip content={"Setup is required for incremental mode"}>
            <div>
              <CircleAlertIcon
                className="size-4 text-red-500"
                aria-hidden="true"
              />
            </div>
          </Tooltip>
        </div>
      ) : null}

      <div className="flex items-center gap-1">
        <SyncLabels
          stream={props.stream}
          streamState={streamState}
          mode={mode}
          onIncrementalClick={() => setShowIncrementalSetup(true)}
        />
      </div>

      <TableOptionsMenu>
        {showIncrementalOption && (
          <Menu.Item>
            {({ close }) => (
              <div>
                <Switch.Group>
                  <Tooltip
                    content={
                      mode === "APPEND"
                        ? OptionTooltips.append
                        : OptionTooltips.incremental
                    }
                    maxWidth="300px"
                    placement="left-start"
                  >
                    <div
                      className="flex w-full items-center p-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={(e) => {
                        const checked = !(
                          mode === StreamMode.INCREMENTAL ||
                          mode === StreamMode.APPEND
                        );
                        if (checked) {
                          setShowIncrementalSetup(true);
                          close();
                        } else {
                          e.preventDefault();
                          dispatch({
                            type: "disable_incremental_sync",
                            payload: {
                              stream: props.stream,
                            },
                          });
                        }
                      }}
                    >
                      <Switch.Label className="pointer-events-none grow">
                        {mode === StreamMode.INCREMENTAL
                          ? "Incremental"
                          : mode === StreamMode.APPEND
                            ? "Incremental (APPEND)"
                            : "Set up Incremental sync"}
                      </Switch.Label>
                      <Toggle
                        checked={
                          mode === StreamMode.INCREMENTAL ||
                          mode === StreamMode.APPEND
                        }
                      />
                    </div>
                  </Tooltip>
                </Switch.Group>
              </div>
            )}
          </Menu.Item>
        )}
        {showAlwaysFullOption && (
          <Menu.Item>
            {({ close }) => (
              <div>
                <Switch.Group>
                  <Tooltip
                    content={OptionTooltips.full}
                    maxWidth="300px"
                    placement="left-start"
                  >
                    <div
                      className="flex w-full items-center p-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={(e) => {
                        const checked = !streamState?.fullSyncAlways;
                        if (checked) {
                          e.preventDefault();
                          dispatch({
                            type: "toggle_full_sync",
                            payload: {
                              stream: props.stream,
                            },
                          });
                        } else {
                          if (props.stream.configurableIncremental) {
                            setShowIncrementalSetup(true);
                            close();
                          } else {
                            e.preventDefault();
                            dispatch({
                              type: "toggle_full_sync",
                              payload: {
                                stream: props.stream,
                              },
                            });
                          }
                        }
                      }}
                    >
                      <Switch.Label className="pointer-events-none grow">
                        Always full sync
                      </Switch.Label>
                      <Toggle checked={!!streamState?.fullSyncAlways} />
                    </div>
                  </Tooltip>
                </Switch.Group>
              </div>
            )}
          </Menu.Item>
        )}
        {showFullSyncScheduleOption && (
          <Menu.Item>
            {({ close }) => (
              <div>
                <Switch.Group>
                  <Tooltip
                    content={OptionTooltips.fullSyncSchedule}
                    maxWidth="300px"
                    placement="left-start"
                  >
                    <div
                      className="flex w-full items-center p-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => {
                        // The menu is portalled at the same z-index as the modal, so it has to go
                        // before the modal opens or it covers it.
                        close();
                        setScheduleModal({ requiredForCaptureDeletes: false });
                      }}
                    >
                      <Switch.Label className="pointer-events-none grow">
                        Scheduled full sync
                      </Switch.Label>
                      <Toggle checked={!!streamState?.fullSyncSchedule} />
                    </div>
                  </Tooltip>
                </Switch.Group>
              </div>
            )}
          </Menu.Item>
        )}
        {showProtectedOption && (
          <Menu.Item>
            <div>
              <Switch.Group>
                <Tooltip
                  content={OptionTooltips.protected}
                  maxWidth="300px"
                  placement="left-start"
                >
                  <div
                    className="flex w-full items-center p-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch({
                        type: "toggle_protected_from_full_sync",
                        payload: {
                          sourceStreamName: props.stream.name,
                        },
                      });
                    }}
                  >
                    <Switch.Label className="pointer-events-none grow">
                      Protected
                    </Switch.Label>
                    <Toggle checked={!!streamState?.protectedFromFullSync} />
                  </div>
                </Tooltip>
              </Switch.Group>
            </div>
          </Menu.Item>
        )}
        {showCDCOption && (
          <Menu.Item>
            <div>
              <Switch.Group>
                <Tooltip
                  content={
                    features.changeDataCapture ? (
                      OptionTooltips.changeDataCapture
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center font-medium">
                          CDC is available on the
                          <PlanFeatureBadge planName={PlanName.Business} /> plan
                        </div>
                        {OptionTooltips.changeDataCapture}
                      </div>
                    )
                  }
                  maxWidth="300px"
                  placement="left-start"
                >
                  <div
                    className="flex w-full items-center p-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={(e) => {
                      e.preventDefault();
                      if (features.changeDataCapture) {
                        dispatch({
                          type: "toggle_change_data_capture",
                          payload: {
                            sourceStreamName: props.stream.name,
                            stream: props.stream,
                          },
                        });
                      }
                    }}
                  >
                    <Switch.Label className="pointer-events-none grow">
                      Change Data Capture
                    </Switch.Label>
                    <Toggle
                      checked={!!streamState?.changeDataCaptureEnabled}
                      disabled={!features.changeDataCapture}
                    />
                  </div>
                </Tooltip>
              </Switch.Group>
            </div>
          </Menu.Item>
        )}
        {showSoftDeleteOption && (
          <Menu.Item>
            {({ close }) => (
              <div>
                <Switch.Group>
                  <Tooltip
                    content={
                      features.softDeletes ? (
                        OptionTooltips.softDelete
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center font-medium">
                            Capturing deletes is available on the
                            <PlanFeatureBadge
                              planName={PlanName.Premium}
                            />{" "}
                            plan
                          </div>
                          {OptionTooltips.softDelete}
                        </div>
                      )
                    }
                    maxWidth="300px"
                    placement="left-start"
                  >
                    <div
                      className="flex w-full items-center p-2 text-sm hover:bg-gray-100 hover:dark:bg-gray-700"
                      onClick={(e) => {
                        if (!features.softDeletes) {
                          e.preventDefault();
                          return;
                        }
                        const needsSchedule =
                          !streamState?.softDeleteEnabled &&
                          !!streamState &&
                          !hasFullSyncToReconcileAgainst(
                            props.stream,
                            streamState,
                          );
                        if (!needsSchedule) {
                          e.preventDefault();
                        }
                        dispatch({
                          type: "toggle_soft_delete",
                          payload: {
                            sourceStreamName: props.stream.name,
                          },
                        });
                        if (needsSchedule) {
                          // Same as above: the menu would otherwise cover the modal it opens.
                          close();
                          setScheduleModal({ requiredForCaptureDeletes: true });
                        }
                      }}
                    >
                      <Switch.Label className="pointer-events-none grow">
                        Capture deletes <BetaFeatureText />
                      </Switch.Label>
                      <Toggle
                        checked={!!streamState?.softDeleteEnabled}
                        disabled={!features.softDeletes}
                      />
                    </div>
                  </Tooltip>
                </Switch.Group>
              </div>
            )}
          </Menu.Item>
        )}
        <Menu.Item>
          <div>
            <Switch.Group>
              <Tooltip
                content={OptionTooltips.historyTable(
                  props.stream.substreams.some(
                    (sub) => !streamState?.excludedSubstreams?.includes(sub.id),
                  ),
                )}
                maxWidth="300px"
                placement="left-start"
              >
                <div
                  className="flex w-full items-center p-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch({
                      type: "toggle_history_table",
                      payload: {
                        sourceStreamName: props.stream.name,
                      },
                    });
                  }}
                >
                  <Switch.Label className="pointer-events-none grow">
                    {props.stream.substreams.length > 0
                      ? "History tables"
                      : "History table"}
                  </Switch.Label>
                  <Toggle checked={!!streamState?.enabledHistoryTable} />
                </div>
              </Tooltip>
            </Switch.Group>
          </div>
        </Menu.Item>
      </TableOptionsMenu>
      <Modal
        isOpen={showIncrementalSetup}
        onClose={() => setShowIncrementalSetup(false)}
      >
        <ModalCloseButton />
        {showIncrementalSetup && (
          <IncrementalSetup
            stream={props.stream}
            sourceIntegration={props.sourceIntegration}
            schema={props.schema}
            onClose={() => setShowIncrementalSetup(false)}
            currentMode={mode === "APPEND" ? "APPEND" : "MERGE"}
          />
        )}
      </Modal>
      {scheduleModal && (
        <FullSyncScheduleModal
          streamName={props.stream.name}
          syncInterval={props.syncInterval}
          currentSchedule={streamState?.fullSyncSchedule ?? null}
          requiredForCaptureDeletes={scheduleModal.requiredForCaptureDeletes}
          onSave={(cron) => {
            dispatch({
              type: "set_full_sync_schedule",
              payload: {
                sourceStreamName: props.stream.name,
                stream: props.stream,
                cron,
              },
            });
            setScheduleModal(null);
          }}
          onRemove={() => {
            dispatch({
              type: "set_full_sync_schedule",
              payload: {
                sourceStreamName: props.stream.name,
                stream: props.stream,
                cron: null,
              },
            });
            setScheduleModal(null);
          }}
          onCancel={() => {
            // Capturing deletes was switched on ahead of the schedule it needs, so leaving without
            // one has to take it back off.
            if (
              scheduleModal.requiredForCaptureDeletes &&
              streamState?.softDeleteEnabled
            ) {
              dispatch({
                type: "toggle_soft_delete",
                payload: { sourceStreamName: props.stream.name },
              });
            }
            setScheduleModal(null);
          }}
        />
      )}
    </div>
  );
};

const IncrementalSetup = (props: {
  stream: Stream;
  sourceIntegration: AvailableIntegrationFragment;
  schema?: StreamSchema;
  currentMode?: "APPEND" | "MERGE";
  onClose: () => void;
}) => {
  const [state, dispatch] = useSourceStreamsState();
  const streamState = state[props.stream.name];

  const filteredSchemaOptions = useMemo(
    () =>
      props.schema?.fields?.filter(
        (option) =>
          !streamState?.excludedColumns.some((c) => c === option.name) &&
          !streamState?.hashedColumns.some((c) => c === option.name),
      ) ?? [],
    [props.schema, streamState?.excludedColumns, streamState?.hashedColumns],
  );

  const [cursor, setCursor] = useState<string | null>(() => {
    if (streamState?.incrementalPointerId) {
      return streamState.incrementalPointerId;
    }
    const incrementalConfig = resolveIncrementalConfig(
      props.sourceIntegration.id,
      props.schema,
    );
    return incrementalConfig?.cursor ?? null;
  });

  const [additionalCursors, setAdditionalCursors] = useState<string[]>(
    () => streamState?.additionalIncrementalPointerIds ?? [],
  );
  const cursorType = filteredSchemaOptions.find(
    (option) => option.name === cursor,
  )?.type;
  const additionalCursorOptions = filteredSchemaOptions.filter(
    (option) => option.name !== cursor && isEqual(option.type, cursorType),
  );
  const chosenAdditionalCursors = props.stream
    .supportsMultipleIncrementalPointers
    ? keptAdditionalCursors(
        additionalCursors,
        cursor,
        props.schema ? additionalCursorOptions : undefined,
      )
    : [];
  const additionalCursorsInputId = `additional-cursors-${props.stream.name}`;

  const [primaryKeys, setPrimaryKeys] = useState<string[] | undefined>(() => {
    if (streamState?.primaryKeys && streamState.primaryKeys.length > 0) {
      return streamState.primaryKeys;
    }
    return props.schema?.primaryKeys ?? undefined;
  });

  const [mode, setMode] = useState<"APPEND" | "MERGE">(
    props.currentMode ?? "MERGE",
  );

  const createSelectOption = (value: string | null | undefined) =>
    value ? { value, label: value } : null;

  const submitIncremental = () => {
    // If CDC is active, disable it when enabling incremental
    if (streamState?.changeDataCaptureEnabled) {
      dispatch({
        type: "toggle_change_data_capture",
        payload: {
          sourceStreamName: props.stream.name,
          stream: props.stream,
        },
      });
    }
    if (mode === "MERGE") {
      if (!primaryKeys?.length || !cursor) return;
      dispatch({
        type: "toggle_incremental_sync",
        payload: {
          stream: props.stream,
          mode,
          primaryKeys: primaryKeys,
          cursor: cursor,
          additionalCursors: chosenAdditionalCursors,
        },
      });
    } else {
      if (!cursor) return;
      dispatch({
        type: "toggle_incremental_sync",
        payload: {
          stream: props.stream,
          mode,
          cursor: cursor,
          additionalCursors: chosenAdditionalCursors,
        },
      });
    }
    props.onClose();
  };

  const modeOptions = {
    MERGE: "MERGE (Recommended)",
    APPEND: "APPEND",
  };

  const submitDisabled = useMemo(() => {
    if (mode === "MERGE")
      return !cursor || !primaryKeys || primaryKeys.length === 0;
    if (mode === "APPEND") return !cursor;
  }, [cursor, mode, primaryKeys]);

  if (!streamState) return null;

  return (
    <>
      <ModalHeader>Setup incremental sync</ModalHeader>
      <ModalBody className="flex flex-col gap-6">
        <div>
          <label className="mb-1 block text-xs">Mode</label>
          <Select
            value={{ value: mode, label: modeOptions[mode] }}
            options={(["MERGE", "APPEND"] as ("MERGE" | "APPEND")[]).map(
              (value) => ({
                value,
                label: modeOptions[value],
              }),
            )}
            onChange={(item: { value: "MERGE" | "APPEND" }) => {
              setMode(item.value);
            }}
            isClearable={false}
          />
        </div>
        {mode === "MERGE" && (
          <div className="text-sm">
            Pick primary key and cursor field to enable incremental sync for{" "}
            {props.stream.name}. Only changed and new rows will be updated on
            subsequent syncs.
          </div>
        )}
        {mode === "APPEND" && (
          <div className="text-sm">
            Pick cursor field to enable incremental sync in APPEND mode for{" "}
            {props.stream.name}. APPEND mode will never update rows if they
            change, only add new rows.
          </div>
        )}
        {mode === "MERGE" && (
          <div>
            <label className="mb-1 block text-xs">Primary key</label>
            <Select
              placeholder="Select primary key..."
              isMulti
              value={primaryKeys?.map(createSelectOption)}
              options={filteredSchemaOptions
                .filter((o) => o.name !== "xmin")
                .map((option) => ({
                  value: option.name,
                  label: option.name,
                }))}
              onChange={(items: { value: string }[] | null) => {
                setPrimaryKeys(items?.map((option) => option.value));
              }}
            />
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs">Cursor</label>
          {props.sourceIntegration?.id.includes("ftp") ? (
            //Hardcoded cursor value for FTP syncs
            <Select
              value={createSelectOption(cursor)}
              isClearable={false}
              autoSelectOnSingleOption
              options={[{ value: "_modified", label: "_modified" }]}
              onChange={(item: { value: string } | null) => {
                setCursor(item?.value ?? null);
              }}
            />
          ) : props.sourceIntegration?.id.includes("shopify") ? (
            //Hardcoded cursor value for Shopify custom report syncs
            <Select
              value={createSelectOption(cursor)}
              isClearable={false}
              autoSelectOnSingleOption
              options={[{ value: "since", label: "since" }]}
              onChange={(item: { value: string } | null) => {
                setCursor(item?.value ?? null);
              }}
            />
          ) : (
            <Select
              placeholder="Select one..."
              value={createSelectOption(cursor)}
              options={filteredSchemaOptions.map((option) => ({
                value: option.name,
                label: option.name,
              }))}
              onChange={(item: { value: string } | null) => {
                setCursor(item?.value ?? null);
              }}
            />
          )}
        </div>
        {props.stream.supportsMultipleIncrementalPointers && cursor && (
          <div>
            <label
              htmlFor={additionalCursorsInputId}
              className="mb-1 block text-xs"
            >
              Additional cursors (optional)
            </label>
            <Select
              inputId={additionalCursorsInputId}
              placeholder="Select columns..."
              isMulti
              value={chosenAdditionalCursors.map(createSelectOption)}
              options={additionalCursorOptions.map((option) => ({
                value: option.name,
                label: option.name,
              }))}
              onChange={(items: { value: string }[] | null) => {
                setAdditionalCursors(
                  items?.map((option) => option.value) ?? [],
                );
              }}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              A row is synced again when any cursor is later than the last sync,
              e.g. a row&apos;s created_at and closed_at when the table has no
              updated_at column.
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <PrimaryButton
          onClick={() => submitIncremental()}
          disabled={submitDisabled}
        >
          {mode === "APPEND" ? "Enable append sync" : "Enable incremental sync"}
        </PrimaryButton>
        <SecondaryButton onClick={() => props.onClose()}>
          Cancel
        </SecondaryButton>
      </ModalFooter>
    </>
  );
};

const ConnectingLine = (props: { isParent?: boolean }) => (
  <div
    className="absolute w-auto"
    style={{
      top: "18px",
      left: -6,
      height: "calc(100% - 35px)",
    }} // Fine tune wrapper to the center-left of checkboxes
  >
    <div
      className="absolute border-b border-gray-200 dark:border-gray-500"
      style={{ width: "8px" }}
    />
    {props.isParent && (
      <div className="absolute h-full border-l border-gray-200 dark:border-gray-500" />
    )}
  </div>
);

type SubstreamAvroSchema = {
  name: string;
  type: string;
  fields: { name: string; type: string }[];
};

const SubStreamItem = (props: {
  substream: SubStream;
  isLast: boolean;
  search: string;
  stream: Stream;
  checked?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [state, dispatch] = useSourceStreamsState();
  const streamState = state[props.stream.name];

  const isExcluded = streamState?.excludedSubstreams?.includes(
    props.substream.id,
  );
  const isChecked = !!props.checked && !isExcluded;
  const isDisabled = !props.checked;

  const onToggle = (checked: boolean) => {
    dispatch({
      type: "toggle_substream",
      payload: {
        sourceStreamName: props.stream.name,
        substreamId: props.substream.id,
        checked,
      },
    });
  };

  return (
    <div className="relative">
      <ConnectingLine />
      <div className="group flex items-center gap-4 px-4 py-2">
        <Checkbox
          checked={isChecked}
          disabled={isDisabled}
          onChange={(e) => {
            if (!isDisabled) {
              onToggle(e.target.checked);
            }
          }}
        />
        <div
          className={`flex grow items-center gap-2 ${props.stream.selectable ? "" : "text-gray-300 dark:text-gray-600"}`}
        >
          <OpenButton
            open={open}
            disabled={false}
            checked={true}
            onToggle={() => setOpen((prev) => !prev)}
          />
          <div className="min-w-0 grow truncate">
            <HighlightedText
              lineThrough={props.substream.retired}
              highlighted={props.search}
              text={props.substream.destinationTableName}
            />
          </div>
          {props.substream.retired && (
            <DeprecatedHoverCard
              retirementDetails={props.substream.retirementDetails}
            >
              <DeprecatedLabel />
            </DeprecatedHoverCard>
          )}
        </div>
      </div>
      {open && (
        <div className="pb-2 pl-8">
          {(props.substream.avroSchema as SubstreamAvroSchema).fields?.map(
            (field) => (
              <SchemaOption
                key={field.name}
                disabled
                readOnly
                title={field.name}
                isCursor={false}
                isPrimaryKey={false}
                checked={true}
              />
            ),
          )}
          <WeldManagedSchemaOptions stream={props.stream} />
        </div>
      )}
    </div>
  );
};

const HistoryStreamItem = (props: {
  historyStream: HistoryStream;
  isLast: boolean;
  search: string;
  stream: Stream;
}) => {
  const [state] = useSourceStreamsState();
  const streamState = state[props.stream.name];

  const isExcluded =
    props.historyStream.substreamId != null &&
    !!streamState?.excludedSubstreams?.includes(
      props.historyStream.substreamId,
    );

  const parentTableName = props.historyStream.substreamId
    ? (props.stream.substreams.find(
        (sub) => sub.id === props.historyStream.substreamId,
      )?.destinationTableName ?? props.stream.name)
    : props.stream.name;

  return (
    <div className="relative">
      <ConnectingLine />
      <div className="group flex items-center gap-4 px-4 py-2">
        <Checkbox checked={!isExcluded} disabled />
        <HistoryTableHoverCard
          tableName={props.historyStream.destinationTableName}
          parentTableName={parentTableName}
        >
          <div className="flex grow items-center gap-2 text-muted-foreground">
            <div className="flex h-5 min-w-5 items-center justify-center">
              <History className="w-4 shrink-0" />
            </div>
            <div className="min-w-0 grow truncate">
              <HighlightedText
                highlighted={props.search}
                text={props.historyStream.destinationTableName}
              />
            </div>
          </div>
        </HistoryTableHoverCard>
      </div>
    </div>
  );
};

const OpenButton = (props: {
  open: boolean;
  disabled: boolean;
  onToggle: (e: React.MouseEvent<HTMLButtonElement>) => void;
  checked: boolean;
}) => {
  return (
    <IconButton
      type="button"
      variant="ghost"
      size="xs"
      disabled={props.disabled}
      onClick={(e) => !props.disabled && props.onToggle(e)}
      icon={
        <ChevronRightIcon
          className={classNames(
            "w-4 transition-transform",
            props.open && !props.disabled && "rotate-90",
            !props.checked && "invisible",
          )}
        />
      }
    />
  );
};

const StreamSchemaOption = (props: {
  option: AvroSchemaFieldDto;
  stream: Stream;
  schema: SchemaDto;
  required: boolean;
}) => {
  const [state, dispatch] = useSourceStreamsState();

  const streamState = state[props.stream.name];

  const hashed = !!streamState?.hashedColumns.some(
    (c) => c === props.option.name,
  );

  const isPrimaryKey = useMemo(() => {
    if (props.stream.configurableIncremental) {
      return streamState?.primaryKeys.includes(props.option.name) ?? false;
    }
    if (props.schema.primaryKeys?.includes(props.option.name)) {
      return true;
    }
    return props.stream.primaryKeyNames.includes(props.option.name);
  }, [
    props.option.name,
    props.schema.primaryKeys,
    props.stream.configurableIncremental,
    props.stream.primaryKeyNames,
    streamState?.primaryKeys,
  ]);

  const isCustomCursor =
    streamState?.incrementalPointerId === props.option.name ||
    !!streamState?.additionalIncrementalPointerIds?.includes(props.option.name);
  const checked =
    (streamState && !streamState.excludedColumns.includes(props.option.name)) ||
    props.required ||
    isPrimaryKey ||
    isCustomCursor;
  const disabled = isPrimaryKey || isCustomCursor;
  const showHashButton = streamState && !disabled;

  return (
    <SchemaOption
      title={props.option.name}
      checked={checked}
      hashed={hashed}
      disabled={isPrimaryKey || isCustomCursor}
      isCursor={isCustomCursor}
      isPrimaryKey={isPrimaryKey}
      showHashButton={showHashButton}
      onChangeHashed={(hashed) => {
        dispatch({
          type: "change_hashed_column",
          payload: {
            columnName: props.option.name,
            sourceStreamName: props.stream.name,
            hashed,
          },
        });
      }}
      onChange={(checked) => {
        dispatch({
          type: "change_excluded_column",
          payload: {
            checked,
            columnName: props.option.name,
            sourceStreamName: props.stream.name,
          },
        });
      }}
      readOnly={!streamState}
      required={props.required}
    />
  );
};

/**
 * The columns Weld maintains rather than the source. They are always written, so they show as
 * selected and cannot be deselected.
 */
const WeldManagedSchemaOptions = (props: { stream: Stream }) => {
  const [state] = useSourceStreamsState();
  const streamState = state[props.stream.name];

  return (
    <>
      {weldManagedFields({
        changeDataCaptureEnabled: streamState?.changeDataCaptureEnabled,
        softDeleteEnabled: streamState?.softDeleteEnabled,
      }).map((field) => (
        <SchemaOption
          key={field.name}
          title={field.name}
          checked
          disabled
          readOnly
          isCursor={false}
          isPrimaryKey={false}
          note={{ label: "Weld", description: field.description }}
        />
      ))}
    </>
  );
};

const SchemaOption = (props: {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  onChangeHashed?: (hashed: boolean) => void;
  title: string;
  hashed?: boolean;
  isPrimaryKey: boolean;
  isCursor: boolean;
  showHashButton?: boolean;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  note?: { label: string; description: string };
}) => {
  return (
    <div
      className={classNames(
        "group flex items-center overflow-hidden px-4 py-1",
        !props.readOnly &&
          "rounded-xs hover:bg-gray-100 dark:hover:bg-gray-700",
      )}
      onClick={() => {
        if (!props.readOnly && !props.disabled) {
          props.onChange?.(!props.checked);
        }
      }}
    >
      <div
        className={classNames(
          "flex w-full min-w-0 grow items-center space-x-2",
          props.readOnly ? "cursor-default" : "cursor-auto",
        )}
      >
        <Checkbox
          checked={props.checked}
          disabled={props.disabled}
          readOnly={props.readOnly}
          onChange={noop}
          onClick={(e) => {
            e.preventDefault();
          }}
        />
        <div className="truncate" title={props.title}>
          {props.title}
        </div>
        {(props.isPrimaryKey || props.isCursor) && (
          <div className="rounded-xs bg-primary px-1 text-xs whitespace-nowrap text-white">
            {props.isCursor ? "Cursor" : "Primary key"}
          </div>
        )}
        {props.note && (
          <Tooltip content={props.note.description}>
            <div className="rounded-sm bg-gray-200 px-1 text-xs whitespace-nowrap text-gray-600 dark:bg-gray-600 dark:text-gray-200">
              {props.note.label}
              {/* Tooltip is hover-only, so the description would otherwise never be announced. */}
              <span className="sr-only"> — {props.note.description}</span>
            </div>
          </Tooltip>
        )}
      </div>
      {props.required && (
        <div className="rounded-xs bg-primary px-1 text-1xs text-white">
          Required
        </div>
      )}
      {props.showHashButton && (
        <HashButton
          hashed={props.hashed ?? false}
          onChange={props.onChangeHashed}
          disabled={!props.checked}
        />
      )}
    </div>
  );
};

const HashButton = (props: {
  hashed: boolean;
  onChange?: (hashed: boolean) => void;
  disabled?: boolean;
}) => {
  return (
    <Menu
      as="div"
      className="inline-block text-left"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Menu.Button
        as={Button}
        size="xs"
        iconRight={<ChevronDownIcon className="w-4" />}
        disabled={props.disabled}
        type="button"
        className={classNames({
          "invisible group-hover:visible": !props.hashed,
        })}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {props.hashed ? "Hashed" : "Unhashed"}
      </Menu.Button>
      <Menu.Items
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 z-50 mt-2 w-56 origin-top-right overflow-hidden rounded-md border bg-popover shadow-lg focus:outline-hidden"
      >
        <Menu.Item>
          {({ active }) => (
            <button
              className={classNames(
                "group flex w-full flex-col space-y-2 px-2 py-2 text-left text-xs",
                {
                  "bg-gray-50 dark:bg-gray-700": active && props.hashed,
                  "bg-primary text-white": !props.hashed,
                },
              )}
              onClick={() => props.onChange?.(false)}
            >
              <span className="text-xs font-semibold">Unhashed</span>
              <span className="w-full text-left">
                Values in this column will be unchanged when synced
              </span>
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <button
              className={classNames(
                "group flex w-full flex-col space-y-2 px-2 py-2 text-left text-xs",
                {
                  "bg-gray-50 dark:bg-gray-700": active && !props.hashed,
                  "bg-primary text-white": props.hashed,
                },
              )}
              onClick={() => props.onChange?.(true)}
            >
              <span className="text-xs font-semibold">Hashed</span>
              <span className="w-full text-left">
                Values in this column will be hashed before they are synced
              </span>
            </button>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
};
